class CustomerStoriesCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.slides = Array.from(this.querySelectorAll('[data-story-slide]'));
    this.progressSegments = Array.from(this.querySelectorAll('[data-story-progress]'));
    this.progressFills = Array.from(this.querySelectorAll('[data-story-progress-fill]'));
    this.pauseButton = this.querySelector('[data-story-pause]');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.prefersReducedMotion;
    this.pauseOnHover =
      this.dataset.pauseOnHover === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.activeIndex = 0;
    this.userPaused = false;
    this.hoverPaused = false;
    this.pressPaused = false;
    this.longPressTimer = null;
    this.hasPublishedSectionView = false;
    this.isSectionVisible = false;

    if (!this.slides.length) return;

    this.abortController = new AbortController();
    this.bindEvents();
    this.bindVisibilityObserver();
    this.pauseButton && (this.pauseButton.hidden = !this.autoplay);
    this.classList.toggle('is-static', !this.autoplay);
    this.goTo(0, { publishStoryView: false });
  }

  disconnectedCallback() {
    this.clearPressPause();
    this.visibilityObserver?.disconnect();
    this.abortController?.abort();
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.querySelectorAll('[data-story-prev]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.previous();
      }, { signal });
    });

    this.querySelectorAll('[data-story-next]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.next();
      }, { signal });
    });

    this.progressFills.forEach((fill, index) => {
      fill.addEventListener('animationend', () => {
        if (this.autoplay && index === this.activeIndex && !this.isPaused()) {
          this.next({ source: 'autoplay' });
        }
      }, { signal });
    });

    this.pauseButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.syncPausedState();
      this.publishCustomerEvent(
        this.userPaused ? 'oribrif:customer_story_pause' : 'oribrif:customer_story_resume'
      );
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }

      if (event.key === ' ') {
        event.preventDefault();
        if (this.autoplay) {
          this.userPaused = !this.userPaused;
          this.syncPausedState();
        }
      }
    }, { signal });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => {
        this.hoverPaused = true;
        this.syncPausedState();
      }, { signal });

      this.addEventListener('mouseleave', () => {
        this.hoverPaused = false;
        this.syncPausedState();
      }, { signal });
    }

    this.querySelector('.customer-stories-carousel__phone')?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.clearPressPause();
      this.longPressTimer = window.setTimeout(() => {
        this.pressPaused = true;
        this.syncPausedState();
      }, 280);
    }, { signal });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach((eventName) => {
      this.querySelector('.customer-stories-carousel__phone')?.addEventListener(eventName, () => {
        this.clearPressPause();
      }, { signal });
    });

    this.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail?.blockId;
      const selectedIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId || slide.contains(event.target));

      if (selectedIndex >= 0) {
        this.goTo(selectedIndex, { source: 'theme_editor' });
        this.userPaused = true;
        this.syncPausedState();
      }
    }, { signal });
  }

  bindVisibilityObserver() {
    if (!('IntersectionObserver' in window)) {
      this.publishSectionView();
      return;
    }

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry) return;

        this.isSectionVisible = entry.isIntersecting;

        if (entry.isIntersecting) {
          this.publishSectionView();
        }
      },
      { threshold: 0.35 }
    );

    this.visibilityObserver.observe(this);
  }

  publishSectionView() {
    if (this.hasPublishedSectionView) return;

    this.hasPublishedSectionView = true;
    this.publishCustomerEvent('oribrif:customer_stories_view');
    this.publishStoryView('section_view');
  }

  isPaused() {
    return !this.autoplay || this.userPaused || this.hoverPaused || this.pressPaused;
  }

  syncPausedState() {
    const paused = this.isPaused();

    this.classList.toggle('is-paused', paused);
    this.pauseButton?.classList.toggle('is-paused', this.userPaused);

    if (this.pauseButton) {
      this.pauseButton.setAttribute(
        'aria-label',
        this.userPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel
      );
    }
  }

  previous() {
    this.clearPressPause();
    this.publishCustomerEvent('oribrif:customer_story_previous', { navigation_source: 'manual' });
    this.goTo(this.activeIndex - 1, { source: 'manual' });
  }

  next(options = {}) {
    this.clearPressPause();
    const source = options.source || 'manual';

    if (source === 'manual') {
      this.publishCustomerEvent('oribrif:customer_story_next', { navigation_source: source });
    }

    this.goTo(this.activeIndex + 1, { source });
  }

  clearPressPause() {
    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    if (this.pressPaused) {
      this.pressPaused = false;
      this.syncPausedState();
    }
  }

  goTo(index, options = {}) {
    const nextIndex = (index + this.slides.length) % this.slides.length;
    const previousIndex = this.activeIndex;

    this.activeIndex = nextIndex;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;

      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.resetProgress();
    this.syncPausedState();

    if (options.publishStoryView !== false && this.isSectionVisible && previousIndex !== nextIndex) {
      this.publishStoryView(options.source || 'unknown');
    }
  }

  resetProgress() {
    this.progressFills.forEach((fill) => {
      fill.classList.remove('is-animating');
      fill.style.animationDuration = '';
    });

    this.progressSegments.forEach((segment, segmentIndex) => {
      segment.classList.toggle('is-complete', segmentIndex < this.activeIndex);
    });

    const activeFill = this.progressFills[this.activeIndex];
    const activeSlide = this.slides[this.activeIndex];

    if (!activeFill || !activeSlide || !this.autoplay) return;

    activeFill.style.animationDuration = `${Number(activeSlide.dataset.duration) || 5000}ms`;
    activeFill.getBoundingClientRect();
    activeFill.classList.add('is-animating');
  }

  publishStoryView(source) {
    this.publishCustomerEvent('oribrif:customer_story_view', {
      story_view_source: source,
    });
  }

  publishCustomerEvent(eventName, extraData = {}) {
    if (!window.Shopify?.analytics?.publish) return;

    const activeSlide = this.slides[this.activeIndex];
    const activeStoryIndex = Number(activeSlide?.dataset.storyIndex || this.activeIndex);

    window.Shopify.analytics.publish(eventName, {
      section_id: this.dataset.sectionId || null,
      product_id: this.dataset.productId || null,
      product_handle: this.dataset.productHandle || null,
      product_title: this.dataset.productTitle || null,
      story_count: Number(this.dataset.storyCount || this.slides.length),
      story_index: activeStoryIndex,
      story_position: activeStoryIndex + 1,
      story_id: activeSlide?.dataset.blockId || null,
      story_duration_ms: Number(activeSlide?.dataset.duration || 0),
      customer_name: activeSlide?.dataset.customerName || null,
      customer_handle: activeSlide?.dataset.customerHandle || null,
      ...extraData,
    });
  }
}

if (!customElements.get('customer-stories-carousel')) {
  customElements.define('customer-stories-carousel', CustomerStoriesCarousel);
}
